import type React from 'react';

import { resolveLocationToNavigationScreen } from '@gitbook/app-navigation';

import { useHistoryLocation, useNavigation } from '../../../navigation';
import type { UIResolvedContentReference } from '../../ContentReferences';

export type InlineLinkProps = {
    href: string;
    target?: string;
    onClick?: (
        event:
            | React.MouseEvent<HTMLAnchorElement, MouseEvent>
            | React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void;
};

export function useInlineLinkProps(ref: UIResolvedContentReference | null): InlineLinkProps | null {
    const { navigateToHash, navigate } = useNavigation();
    const location = useHistoryLocation();

    if (!ref || !ref.href) {
        return null;
    }

    const href = ref.href;
    const pageLink = href.split('#')[0];

    const pageLinkWithoutTrailingSlash = pageLink?.replace(/\/+$/, '');
    const pathnameWithoutTrailingSlash = location.pathname.replace(/\/+$/, '');
    const isLinkInCurrentPage = pathnameWithoutTrailingSlash === pageLinkWithoutTrailingSlash;

    let linkProps: InlineLinkProps = {
        href: '',
        target: undefined,
        onClick: undefined,
    };

    if (href.startsWith('http')) {
        return { href: href, target: '_blank' };
    }

    if (href.includes('#')) {
        const sectionId = href.split('#')[1] ?? '';

        if (isLinkInCurrentPage) {
            linkProps = { href: sectionId, target: undefined };
        } else {
            linkProps = { href, target: undefined };
        }
    } else {
        linkProps = { href, target: undefined };
    }

    const isInternalLink = !linkProps.target;

    if (isInternalLink) {
        if (isLinkInCurrentPage) {
            // Internal anchor link in the same page: scroll to anchor section in same browser window
            linkProps = {
                ...linkProps,
                onClick: (event) => {
                    navigateToHash(linkProps.href, { scroll: true });
                    event.preventDefault();
                },
            };
        } else {
            // Internal link in different page, open link in same browser window
            const location = new URL(href, window.location.origin);
            const linkScreenState = resolveLocationToNavigationScreen(location);

            linkProps = {
                ...linkProps,
                onClick: (event) => {
                    navigate({ screen: linkScreenState });
                    event.preventDefault();
                },
            };
        }
    }

    // External link: open in new browser tab
    return linkProps;
}
